import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Lock, FileText, FileWarning, FolderLock, ExternalLink } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import api, { API } from "@/lib/api";
import { TenXBrandFooter } from "@/components/TenXBrandFooter";

const fmtSize = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

export default function PublicDataRoom() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [passcode, setPasscode] = useState("");
  const [ndaName, setNdaName] = useState("");
  const [ndaAgreed, setNdaAgreed] = useState(false);
  const [visitId, setVisitId] = useState(null);
  const [entering, setEntering] = useState(false);

  useEffect(() => {
    api.get(`/dr/${token}`).then(({ data }) => setMeta(data))
      .catch(() => setError("This data room link is not available."));
  }, [token]);

  const enter = async (e) => {
    e.preventDefault();
    if (meta.nda_enabled && !ndaAgreed) { alert("Please accept the NDA to continue."); return; }
    setEntering(true);
    try {
      const { data } = await api.post(`/dr/${token}/access`, { email, passcode, nda_accepted: ndaAgreed, signer_name: ndaName });
      setVisitId(data.visit_id);
      if (data.documents) setMeta((m) => ({ ...m, documents: data.documents }));
    } catch (err) { alert(err?.response?.data?.detail || "Access denied"); }
    finally { setEntering(false); }
  };

  const openDoc = (doc) => window.open(`${API}/dr/${token}/doc/${doc.id}?visit=${visitId}`, "_blank");

  return (
    <div className="flex min-h-screen flex-col bg-slate-100">
      <header className="border-b bg-background">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-3">
          <Link to="/"><Logo /></Link>
          {meta && <span className="text-xs text-muted-foreground">Shared by {meta.company_name || "a founder"}</span>}
        </div>
      </header>

      <main className="mx-auto w-full max-w-4xl flex-1 px-4 py-8">
        {error ? (
          <div className="mx-auto mt-16 max-w-md rounded-xl border border-dashed bg-background p-10 text-center" data-testid="dr-error">
            <FileWarning className="mx-auto h-8 w-8 text-muted-foreground" />
            <h1 className="mt-3 h-display text-lg font-semibold text-foreground">{error}</h1>
            <p className="mt-2 text-sm text-muted-foreground">Ask the sender for a fresh link.</p>
          </div>
        ) : !meta ? (
          <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : !visitId ? (
          <Card className="mx-auto mt-12 w-full max-w-sm rounded-xl border bg-background p-6" data-testid="dr-gate">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><Lock className="h-5 w-5" /></div>
            <h1 className="mt-3 h-display text-lg font-semibold text-foreground">{meta.name}</h1>
            {meta.description && <p className="mt-0.5 text-xs text-muted-foreground">{meta.description}</p>}
            <form onSubmit={enter} className="mt-4 space-y-3">
              {meta.require_email && (
                <div><Label>Your email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1" data-testid="dr-email" /></div>
              )}
              {meta.has_passcode && (
                <div><Label>Passcode</Label><Input value={passcode} onChange={(e) => setPasscode(e.target.value)} required className="mt-1" data-testid="dr-passcode" /></div>
              )}
              {meta.nda_enabled && (
                <div className="rounded-lg border bg-slate-50 p-3" data-testid="dr-nda">
                  <div className="mb-1 text-xs font-semibold text-foreground">Confidentiality agreement</div>
                  <div className="max-h-32 overflow-y-auto whitespace-pre-wrap rounded border bg-white p-2 text-[11px] leading-relaxed text-slate-600" data-testid="dr-nda-text">
                    {meta.nda_text || "By continuing you agree to keep all materials in this data room strictly confidential."}
                  </div>
                  <div className="mt-2"><Input value={ndaName} onChange={(e) => setNdaName(e.target.value)} placeholder="Type your full name" required className="h-8 text-xs" data-testid="dr-nda-name" /></div>
                  <label className="mt-2 flex cursor-pointer items-start gap-2 text-[11px] text-foreground">
                    <Checkbox checked={ndaAgreed} onCheckedChange={(v) => setNdaAgreed(!!v)} className="mt-0.5" data-testid="dr-nda-agree" />
                    <span>I have read and agree to the confidentiality terms above.</span>
                  </label>
                </div>
              )}
              <Button type="submit" disabled={entering || (meta.nda_enabled && !ndaAgreed)} className="w-full gap-2" data-testid="dr-enter">
                {entering ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Enter data room
              </Button>
            </form>
          </Card>
        ) : (
          <div data-testid="dr-contents">
            <div className="mb-5 flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><FolderLock className="h-5 w-5" /></div>
              <div>
                <h1 className="h-display text-xl font-semibold text-foreground">{meta.name}</h1>
                {meta.description && <p className="text-xs text-muted-foreground">{meta.description}</p>}
              </div>
            </div>
            {meta.documents.length === 0 ? (
              <p className="text-sm text-muted-foreground">No documents have been added to this room yet.</p>
            ) : (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {meta.documents.map((d) => (
                  <button key={d.id} onClick={() => openDoc(d)}
                    className="flex items-center justify-between rounded-lg border bg-background p-3 text-left transition-colors hover:border-primary/40"
                    data-testid={`dr-doc-${d.id}`}>
                    <div className="flex min-w-0 items-center gap-2">
                      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-foreground">{d.name}</div>
                        <div className="text-[11px] text-muted-foreground">{d.category}{d.size ? ` · ${fmtSize(d.size)}` : ""}</div>
                      </div>
                    </div>
                    <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      <TenXBrandFooter context="data-room" className="bg-background" notice="Access is tracked" />
    </div>
  );
}
